import { useEffect, useRef, type RefObject } from "react";

import { EDGE_PAGE_DESKTOP_PX, EDGE_PAGE_FIRST_MS, EDGE_PAGE_MS } from "./touch-gesture-util.js";

/**
 * Desktop counterpart to the touch move-loop's edge paging: while a calendar
 * block (or an inbox item) is being dragged with native HTML5 drag-and-drop and
 * the cursor sits inside a thin strip at the very left/right edge of the timed
 * grid, advance the visible day window one day at a time — so a task can be
 * dragged across weeks without dropping and re-grabbing.
 *
 * The strip is a THIN fixed `EDGE_PAGE_DESKTOP_PX` (a mouse is precise, so no
 * column-wide slack is needed like on touch) — most of the edge day stays
 * droppable; paging only fires when the cursor is jammed against the edge. The
 * first step waits EDGE_PAGE_FIRST_MS (a deliberate dwell) so a quick drop still
 * works; then it repeats while held. Listeners are window-level so moving off
 * the grid reliably stops the held interval.
 */
export function useEdgeAutoPage(
  areaRef: RefObject<HTMLElement | null>,
  onDayShift: ((delta: number) => void) | undefined,
): void {
  const dirRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shiftRef = useRef(onDayShift);
  shiftRef.current = onDayShift;

  useEffect(() => {
    function stop() {
      dirRef.current = 0;
      if (timerRef.current != null) { clearTimeout(timerRef.current); timerRef.current = null; }
    }
    function arm(dir: number) {
      if (dir === dirRef.current) return;
      stop();
      if (dir === 0) return;
      dirRef.current = dir;
      // Dwell, then fire and repeat — a quick graze through the edge never pages.
      timerRef.current = setTimeout(function tick() {
        shiftRef.current?.(dir);
        timerRef.current = setTimeout(tick, EDGE_PAGE_MS);
      }, EDGE_PAGE_FIRST_MS);
    }
    function onDragOver(e: DragEvent) {
      const area = areaRef.current;
      if (!area || !shiftRef.current) { stop(); return; }
      const r = area.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right
        && e.clientY >= r.top && e.clientY <= r.bottom;
      const zone = EDGE_PAGE_DESKTOP_PX;
      arm(!inside ? 0 : e.clientX < r.left + zone ? -1 : e.clientX > r.right - zone ? 1 : 0);
    }
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragend", stop);
    window.addEventListener("drop", stop, true);
    return () => {
      stop();
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragend", stop);
      window.removeEventListener("drop", stop, true);
    };
  }, [areaRef]);
}
